import React, {useEffect, useState} from 'react';
import {SaveCancelEditButtons} from "../SaveCancelEditButtons";
import {TabbedPageHeader} from "../TabbedPageHeader";
import {MoreActionsDropdown} from "../MoreActionsDropdown";
import {space} from "../../provider_api";
import {useRpcSender} from "../../util/RpcSender";
import {RpcState} from "../../util/RpcState";
import {EditableContext} from "../Editable/EditableContext";
import './KensuObjectEditor.css';
import {useNavigateWithParams} from "../../util/NavUtils";
import IErrorProto = space.kenko.proto.IErrorProto;

type Props<T, ContainingType> = {

  // If set, this is editing and existing id (this one!)
  idParam: string | undefined;

  // Load this T things from the profile.
  getter: (id: string | null | undefined) => T | null | undefined;

  // Typically this object editor shows the object's name (Location X, Package Y) and a back
  // button when in edit mode. But the business profile is a singleton, and we don't want to
  // show the header at all. 
  hideHeaderInEditMode?: boolean

  headerName?: string,

  // Override label for the edit button
  editLabel?: string,

  // Used for the page title when editing the thing.
  getTitle: (entity: T) => string | null | undefined;

  // like "Service"
  entityName: string;

  // if simply adding an 's' does not suffice
  entityNamePlural?: string;

  entityListViewLink: string; // like /catalog/services

  newBlankType: () => T;

  children: React.ReactNode;

  getRpcMethod: (isUpdate: boolean) => string,
  getRpcPayload: (workingCopy: T) => Uint8Array,

  parseRpcResponse: (bytes: Uint8Array) => {
    okay?: boolean,
    updated?: ContainingType | null | undefined,
    errors?: IErrorProto[]
  },
  handleContainerTypeUpdate: (updated: ContainingType | null | undefined) => void,

  // IF this entity is archivable, set this field name. An "archive" option will
  // show up in the editor.
  archiveField?: keyof T,
}

/**
 * Edits updates and archives things - team members, locations,
 * services, packages, price categories.
 *
 * These editor pages all have the same basic functionality.
 *
 * @constructor
 */
export const KensuObjectEditor = <T extends object, ContainingType extends Object>(props: Props<T, ContainingType>) => {

  const isUpdate = !!props.idParam;

  // Whether it's currently being edited. This is always true in 'create'
  // mode and sometimes true in 'update' mode.
  const [editing, setEditing] = useState(!isUpdate);

  const rpcSender = useRpcSender();

  const [rpcState, setRpcState] = useState<RpcState>({state: 'none'});

  const [workingCopy, setWorkingCopy] = useState<T | null | undefined>();
  const [errorContext, setErrorContext] = useState<IErrorProto[]>([]);

  const navigate = useNavigateWithParams();

  // This one is kept in sync with the server. The working copy is mutated
  const initialEntityValue = props.getter(props.idParam);

  useEffect(() => {

    // Initial population of values if this is in edit mode.
    setWorkingCopy(props.idParam ? initialEntityValue : props.newBlankType());

    // Dependency is required on the id param (in case that changes, obviously),
    // but more subtle is the dependency on initial entity value; this changes
    // when an RPC is sent to save the thing. Specifically this is important for
    // archiving, so the change is reflected here in the editor.
  }, [props.idParam, initialEntityValue])

  const submit = (entity: T | null | undefined) => {

    if (!entity) {
      return;
    }

    setErrorContext([])
    setRpcState({state: 'pending'});

    // They all conveniently share the same response type
    rpcSender.send(props.getRpcMethod(isUpdate), props.getRpcPayload(entity))
        .then(bytes => {

          let response = props.parseRpcResponse(bytes);

          setErrorContext(response.errors ?? []);
          setRpcState({state: !!response.errors?.length ? 'error' : 'success'})

          if (!response.okay) {
            console.log('response not okay')
            setRpcState({state: 'error', message: 'Server error. Please try again later.'})
            return;
          }

          if (isUpdate) {
            setRpcState({state: 'success'})
          }

          if (response.updated) {
            props.handleContainerTypeUpdate(response.updated);

            if (isUpdate) {
              // Set the message that it was updated
              setRpcState({state: 'success'});
              setEditing(false);

            } else {
              // Leave the page.
              navigate(props.entityListViewLink);
            }
          }
        }).catch(reason => {
          setRpcState({state: 'error', message: 'Network error, please try again later'})
          console.warn(reason);
        }
    )
  }

  const setArchived = (archived: boolean) => {

    if (!props.archiveField) {
      return;
    }

    if (archived && !window.confirm(`Are you sure you want to archive '${initialObjectName}'?`)) {
      return;
    }

    if (!archived && !window.confirm(`Are you sure you want to unarchive '${initialObjectName}'?`)) {
      return;
    }

    submit({...workingCopy, [props.archiveField]: archived} as T);
  }

  // I guess the working copy can take some time to load.
  if (!workingCopy /*|| !businessProfile*/) {
    return <></>;
  }

  // For updates, this is the name of the thing originally being edited.
  // Avoids weirdness when changing the name.
  const initialObjectName = initialEntityValue && props.getTitle(initialEntityValue);

  const archived = props.archiveField && workingCopy && workingCopy[props.archiveField];

  return <>

  <div style={{flex: 0}}>

    {isUpdate && !props.hideHeaderInEditMode && <TabbedPageHeader
        pageTitle={
          <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
            {initialObjectName}
            {archived && <span className={'Badge'}>ARCHIVED</span>}

          </div>}
        buttons={[
          {
            text: `Back to ` + (props.entityNamePlural ? props.entityNamePlural : props.entityName + 's'),
            link: props.entityListViewLink,
            isSecondary: true
          },
        ]}/>}

    {!isUpdate && <TabbedPageHeader pageTitle={`Add a new ${props.entityName.toLowerCase()}`}
                                    buttons={[
                                      {text: 'Cancel', link: props.entityListViewLink, isSecondary: true},
                                      {
                                        text: 'Save',
                                        rpcState: rpcState,
                                        onClick: () => submit(workingCopy)
                                      },
                                    ]}/>}
  </div>
  
  <div className={'ProviderToolAreaContent'}>

    <div style={{display: 'flex', gap: 10}}>
      <div style={{flex: 1}}>
        <h2>{props.headerName || 'Details'}</h2>
      </div>

      {/* Archived products don't get the "edit" button" */}
      {isUpdate && !archived && <SaveCancelEditButtons
          isEditing={editing}
          rpcState={rpcState}
          editLabel={props.editLabel}
          onEdit={() => setEditing(true)}
          onSave={() => submit(workingCopy)}
          errorText={errorContext.length > 0 ? 'Please check for errors below' : undefined}
          onCancel={() => {
            setWorkingCopy(initialEntityValue);
            setEditing(false);
            setErrorContext([]);
          }}/>}

      {/* archive is only available when you are viewing a specific item,
            it is unavailable if you are actively editing the item */}
      {isUpdate && !editing && props.archiveField && <MoreActionsDropdown options={[
        {
          label: archived ? `Unarchive this ${props.entityName.toLowerCase()}` :
              `Archive this ${props.entityName.toLowerCase()}`,
          onSelected: () => {
            if (props.archiveField) {
              setArchived(!!workingCopy && !workingCopy[props.archiveField])
            }
          }
        }
      ]}/>}

    </div>

    <EditableContext.Provider value={{
      mode: isUpdate ? 'update' : 'create',
      editing: editing,
      workingCopy: workingCopy,
      setWorkingCopy: setWorkingCopy,
      errors: errorContext
    }}>
      {props.children}
    </EditableContext.Provider>
  </div>
  </>
};

